import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeacherAbsenceModal } from '../../../../components/Absences/modals/TeacherAbsenceModal';
import { TeacherAbsencesTable } from '../../../../components/Absences/tables/TeacherAbsencesTable';
import { useConfirmWithCheckbox } from '../../../../hooks/useConfirmWithCheckbox';
import { useMemorizedCacheTags } from '../../../../hooks/useMemorizedCacheTag';
import { useDeleteAbsenceMutation } from '../../../../types/planung-graphql-client-defs';
import { showErrorToast, showSuccessCreateToast } from '../../../../utils/toast';

export const PlanTeacherAbsences = observer(() => {
  const { t } = useTranslation();
  const deleteAbsenceContext = useMemorizedCacheTags(['ABSENCES', 'EVENT']);

  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedAbsenceUuid, setSelectedAbsenceUuid] = useState<string | null>(null);

  const [, remove] = useDeleteAbsenceMutation();

  const { confirm: confirmDelete, ConfirmationDialog } = useConfirmWithCheckbox({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', { type: t('absences.title', { count: 1 }), context: 'female', count: 1 }),
    defaultConfirmText: t('delete.delete'),
    checkboxText: t('absences.removeSubstitutions'),
    checkboxDefaultChecked: true,
  });

  async function handleDelete(uuid: string) {
    const { checked, confirmed } = await confirmDelete();
    if (!confirmed) return;

    setLoading(true);

    const { error } = await remove(
      {
        input: {
          uuids: [uuid],
        },
        removeSubstitutions: checked,
      },
      deleteAbsenceContext,
    );

    if (error) {
      showErrorToast(error);
    } else {
      showSuccessCreateToast();
    }

    setLoading(false);
  }

  return (
    <>
      <TeacherAbsencesTable
        loading={loading}
        onAdd={() => setIsModalOpen(true)}
        onEdit={(uuid) => {
          setIsModalOpen(true);
          setSelectedAbsenceUuid(uuid);
        }}
        onDelete={handleDelete}
      />

      <TeacherAbsenceModal
        selectedAbsenceUuid={selectedAbsenceUuid}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedAbsenceUuid(null);
        }}
      />
      <ConfirmationDialog />
    </>
  );
});
